import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BizUnitSelect from '../../../Core/Components/Mini/BizUnitSelect';
import { useLaxhjalpenStudentList } from '../../../Core/Hooks/useLaxhjalpenStudentList';
import { LaxhjalpenAccountStatus } from '../../../Core/Types/laxhjalpenStudentList';
import './laxhjalpen-student-list.scss';

const translationKey = 'ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.STUDENT_LIST';

const matches = (isActive: boolean, status: LaxhjalpenAccountStatus) =>
	status === 'all' || (status === 'active' ? isActive : !isActive);
const value = (input?: string) => input?.trim() || '—';

export default function LaxhjalpenStudentListPanel() {
	const { t } = useTranslation();
	const text = (key: string) => t(`${translationKey}.${key}`);
	const [unitId, setUnitId] = useState(-1);
	const [studentStatus, setStudentStatus] = useState<LaxhjalpenAccountStatus>('all');
	const [guardianStatus, setGuardianStatus] = useState<LaxhjalpenAccountStatus>('all');
	const { data, statusState, refetch } = useLaxhjalpenStudentList(unitId);
	const students = useMemo(
		() =>
			(data ?? [])
				.filter((student) => matches(student.isActive, studentStatus))
				.map((student) => ({
					...student,
					guardians:
						guardianStatus === 'all'
							? student.guardians
							: student.guardians.filter((guardian) => matches(guardian.isActive, guardianStatus)),
				}))
				.filter((student) => guardianStatus === 'all' || student.guardians.length > 0),
		[data, guardianStatus, studentStatus]
	);

	return (
		<section className='biz-laxhjalpen-student-list' aria-labelledby='biz-laxhjalpen-student-list-title'>
			<h2 id='biz-laxhjalpen-student-list-title' className='biz-laxhjalpen-student-list__title'>
				{text('TITLE')}
			</h2>
			<p className='biz-laxhjalpen-student-list__intro'>{text('INTRO')}</p>
			<div className='biz-laxhjalpen-student-list__filters'>
				<label>
					<span>{text('UNIT')}</span>
					<BizUnitSelect
						accessTypeKey='USERS'
						selectedId={unitId}
						onChange={(unit) => setUnitId(unit?.Id ?? -1)}
						noSelectText={text('SELECT_UNIT')}
						childNoSelectText={text('SELECT_UNIT')}
						direction='horizontal'
					/>
				</label>
				<StatusSelect text={text} label={text('STUDENT_STATUS')} value={studentStatus} onChange={setStudentStatus} />
				<StatusSelect text={text} label={text('GUARDIAN_STATUS')} value={guardianStatus} onChange={setGuardianStatus} />
			</div>
			{unitId <= 0 && <div className='biz-laxhjalpen-student-list__state'>{text('SELECT_UNIT_HINT')}</div>}
			{unitId > 0 && statusState === 'LOADING' && (
				<div className='biz-laxhjalpen-student-list__state' role='status'>
					{text('LOADING')}
				</div>
			)}
			{unitId > 0 && statusState === 'ERROR' && (
				<div className='biz-laxhjalpen-student-list__state biz-laxhjalpen-student-list__state--error' role='alert'>
					{text('ERROR')}{' '}
					<button type='button' onClick={() => refetch()}>
						{text('RETRY')}
					</button>
				</div>
			)}
			{unitId > 0 && statusState === 'SUCCESS' && students.length === 0 && (
				<div className='biz-laxhjalpen-student-list__state'>{text('EMPTY')}</div>
			)}
			{unitId > 0 && statusState === 'SUCCESS' && students.length > 0 && (
				<div className='biz-laxhjalpen-student-list__table-wrap'>
					<table>
						<colgroup>
							<col />
							<col className='biz-laxhjalpen-student-list__unit-column' />
							<col className='biz-laxhjalpen-student-list__guardian-column' />
							<col />
							<col className='biz-laxhjalpen-student-list__mobile-column' />
							<col className='biz-laxhjalpen-student-list__status-column' />
						</colgroup>
						<thead>
							<tr>
								<th>{text('STUDENT')}</th>
								<th>{text('UNIT_NAME')}</th>
								<th>{text('GUARDIAN')}</th>
								<th>{text('EMAIL')}</th>
								<th>{text('MOBILE')}</th>
								<th className='biz-laxhjalpen-student-list__status-cell'>{text('STATUS')}</th>
							</tr>
						</thead>
						{students.map((student) => {
							const guardians = student.guardians.length ? student.guardians : [null];
							return (
								<tbody key={student.id}>
									{guardians.map((guardian, index) => (
										<tr key={guardian?.id ?? 'none'}>
											{index === 0 && (
												<>
													<td rowSpan={guardians.length} data-label={text('STUDENT')}>
														<strong>{value(student.displayName)}</strong>
														<small>
															{value(student.email)} · {value(student.mobile)}
														</small>
														<Status text={text} active={student.isActive} />
													</td>
													<td rowSpan={guardians.length} data-label={text('UNIT_NAME')}>
														{value(student.unitName)}
													</td>
												</>
											)}
											<td data-label={text('GUARDIAN')}>
												{guardian ? value(guardian.displayName) : text('NO_GUARDIANS')}
											</td>
											<td className='biz-laxhjalpen-student-list__contact-cell' data-label={text('EMAIL')}>
												{value(guardian?.email)}
											</td>
											<td className='biz-laxhjalpen-student-list__contact-cell' data-label={text('MOBILE')}>
												{value(guardian?.mobile)}
											</td>
											<td className='biz-laxhjalpen-student-list__status-cell' data-label={text('STATUS')}>
												{guardian && <Status text={text} active={guardian.isActive} />}
											</td>
										</tr>
									))}
								</tbody>
							);
						})}
					</table>
				</div>
			)}
		</section>
	);
}

function StatusSelect({
	text,
	label,
	value: selected,
	onChange,
}: {
	text: (key: string) => string;
	label: string;
	value: LaxhjalpenAccountStatus;
	onChange: (value: LaxhjalpenAccountStatus) => void;
}) {
	return (
		<label>
			<span>{label}</span>
			<select value={selected} onChange={(event) => onChange(event.target.value as LaxhjalpenAccountStatus)}>
				<option value='all'>{text('ALL')}</option>
				<option value='active'>{text('ACTIVE')}</option>
				<option value='inactive'>{text('INACTIVE')}</option>
			</select>
		</label>
	);
}

function Status({ text, active }: { text: (key: string) => string; active: boolean }) {
	return (
		<span
			className={`biz-laxhjalpen-student-list__status biz-laxhjalpen-student-list__status--${active ? 'active' : 'inactive'}`}
		>
			{active ? text('ACTIVE') : text('INACTIVE')}
		</span>
	);
}
